import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import ContentPageFooter from "@/components/ContentPageFooter";

const TermsOfService = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-6 pb-24">
      <Helmet>
        <title>Terms of Service – Ayeris</title>
        <meta name="description" content="Read the Ayeris terms of service. Understand the rules and conditions for using the Ayeris platform." />
        <link rel="canonical" href="https://ayeris.tv/terms-of-service" />
      </Helmet>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">{t("legal.back")}</span>
      </button>

      <h1 className="text-2xl font-display font-bold mb-2">{t("legal.terms.title")}</h1>
      <p className="text-muted-foreground text-sm mb-8">{t("legal.lastUpdated")}</p>

      <div className="space-y-6 text-sm leading-relaxed text-secondary-foreground">
        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s1Title")}</h2>
          <p>{t("legal.terms.s1p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s2Title")}</h2>
          <p>{t("legal.terms.s2p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s3Title")}</h2>
          <p className="mb-2">{t("legal.terms.s3p1")}</p>
          <p>{t("legal.terms.s3p2")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s4Title")}</h2>
          <p className="mb-2">{t("legal.terms.s4p1")}</p>
          <p className="mb-2"><strong className="text-foreground">{t("legal.terms.s4p2Label")}</strong> {t("legal.terms.s4p2")}</p>
          <p><strong className="text-foreground">{t("legal.terms.s4p3Label")}</strong> {t("legal.terms.s4p3")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s5Title")}</h2>
          <p className="mb-2">{t("legal.terms.s5p1")}</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>{t("legal.terms.s5li1")}</li>
            <li>{t("legal.terms.s5li2")}</li>
            <li>{t("legal.terms.s5li3")}</li>
            <li>{t("legal.terms.s5li4")}</li>
            <li>{t("legal.terms.s5li5")}</li>
            <li>{t("legal.terms.s5li6")}</li>
            <li>{t("legal.terms.s5li7")}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s6Title")}</h2>
          <p className="mb-2">{t("legal.terms.s6p1")}</p>
          <p>{t("legal.terms.s6p2")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s7Title")}</h2>
          <p>{t("legal.terms.s7p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s8Title")}</h2>
          <p className="mb-2">{t("legal.terms.s8p1")}</p>
          <p>{t("legal.terms.s8p2")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s9Title")}</h2>
          <p className="mb-2">{t("legal.terms.s9p1")}</p>
          <p className="mb-2">{t("legal.terms.s9p2")}</p>
          <p>{t("legal.terms.s9p3")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s10Title")}</h2>
          <p className="mb-2">{t("legal.terms.s10p1")}</p>
          <p className="mb-2">{t("legal.terms.s10p2")}</p>
          <p>{t("legal.terms.s10p3")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s11Title")}</h2>
          <p>{t("legal.terms.s11p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s12Title")}</h2>
          <p className="mb-2">{t("legal.terms.s12p1")}</p>
          <p>{t("legal.terms.s12p2")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s13Title")}</h2>
          <p>{t("legal.terms.s13p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s14Title")}</h2>
          <p>{t("legal.terms.s14p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s15Title")}</h2>
          <p>{t("legal.terms.s15p1")}</p>
        </section>

        <section>
          <h2 className="text-lg font-display font-semibold text-foreground mb-2">{t("legal.terms.s16Title")}</h2>
          <p>
            {t("legal.terms.s16p1")}{" "}
            <a href="mailto:enquiries@ayeris.tv" className="text-primary hover:underline">enquiries@ayeris.tv</a>
          </p>
        </section>
      </div>

      <ContentPageFooter currentPath="/terms-of-service" />
    </div>
  );
};

export default TermsOfService;
